/* eslint-disable */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Zap } from "lucide-react";
import { useLang } from "./i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLOR_MAP = {
  green: { bg: "#10b981", glow: "rgba(16,185,129,0.6)", text: "text-emerald-400" },
  yellow: { bg: "#facc15", glow: "rgba(250,204,21,0.6)", text: "text-yellow-400" },
  orange: { bg: "#f97316", glow: "rgba(249,115,22,0.6)", text: "text-orange-400" },
  red: { bg: "#ef4444", glow: "rgba(239,68,68,0.6)", text: "text-red-400" },
};

// Map backend risk keys to i18n keys
const RISK_TO_KEY = {
  critical: "risk_critical",
  warning: "risk_warning",
  moderate: "risk_moderate",
  good: "risk_good",
};

// Map weekly bulb color to language-agnostic tier key
const COLOR_TO_TIER = {
  red: "weekly_feb",
  orange: "weekly_bon",
  yellow: "weekly_tres_bon",
  green: "weekly_ekselan",
};

// Map reason label (backend Creole) → i18n key + args regex
const parseReasonLabel = (label, t) => {
  // "Reta (3)" → late_incident_label + count
  let m = /^Reta\s*\((\d+)\)/i.exec(label);
  if (m) return `${t("late_incident_label")} (${m[1]})`;
  // "Retard konsekan (Xj)"
  m = /^Retard konsekan\s*\((\d+)j\)/i.exec(label);
  if (m) return `${t("consecutive_late_label")} (${m[1]}j)`;
  // "Mwa < 50% (X)"
  m = /^Mwa\s*<\s*50%\s*\((\d+)\)/i.exec(label);
  if (m) return `${t("low_month_label")} (${m[1]})`;
  // "Rekiperasyon sik la (X%)"
  m = /^Rekiperasyon sik la\s*\((\d+)%\)/i.exec(label);
  if (m) return `${t("cycle_recovery_label")} (${m[1]}%)`;
  // "Ajisteman manyèl (CEO)"
  if (/^Ajisteman manyèl/i.test(label)) return t("manual_adjust_label");
  return label;
};

const parseReasonDetail = (detail, t) => {
  if (/pwen chak reta/i.test(detail)) return t("late_penalty_detail");
  if (/chak jou apre 3 jou/i.test(detail)) return t("consecutive_late_detail");
  if (/pwen chak mwa/i.test(detail)) return t("low_month_detail");
  if (/\+10 pou.*≥50%/i.test(detail) || /\+20 pou 100%/i.test(detail)) return t("cycle_recovery_detail");
  if (/pwen manyèl/i.test(detail)) return t("manual_adjust_detail");
  return detail;
};

const CreditScoreCard = ({ userId }) => {
  const [data, setData] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    if (!userId) return;
    const load = () => axios.get(`${API}/user/${userId}/credit-score`).then((r) => setData(r.data)).catch(() => {});
    load();
    const tm = setInterval(load, 30000);
    return () => clearInterval(tm);
  }, [userId]);

  if (!data) return null;

  const sc = COLOR_MAP[data.score_color] || COLOR_MAP.red;
  const riskLabelKey = RISK_TO_KEY[data.risk] || "risk_good";

  return (
    <div className="glass-panel p-5 mb-4" data-testid="credit-score-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-white font-bold flex items-center gap-2">
          <Zap className="w-5 h-5 text-emerald-400" /> {t("performance_credit_title")}
        </h3>
        <div className={`text-xs font-bold px-3 py-1 rounded-full ${data.risk === "critical" ? "bg-red-950/50 text-red-300 border border-red-500 animate-pulse" : data.risk === "warning" ? "bg-orange-950/50 text-orange-300 border border-orange-500" : data.risk === "moderate" ? "bg-amber-950/50 text-amber-300 border border-amber-500" : "bg-emerald-950/50 text-emerald-300 border border-emerald-500"}`} data-testid="credit-risk-label">
          {t(riskLabelKey)}
        </div>
      </div>

      {/* S1-S4 Weekly Bulbs */}
      <div className="grid grid-cols-4 gap-2 mb-4" data-testid="weekly-bulbs">
        {data.weekly_bulbs.map((b) => {
          const c = COLOR_MAP[b.color];
          const tierKey = COLOR_TO_TIER[b.color] || "weekly_feb";
          return (
            <div key={b.week} className={`rounded-xl p-3 text-center border ${b.is_on ? "border-zinc-700 bg-zinc-900/70" : "border-zinc-800 bg-zinc-950/50 opacity-40"}`} data-testid={`bulb-${b.week}`}>
              <div className="flex items-center justify-center mb-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                  style={{
                    background: b.is_on ? c.bg : "#27272a",
                    boxShadow: b.is_on ? `0 0 24px ${c.glow}, inset 0 -6px 12px rgba(0,0,0,0.3)` : "none",
                  }}
                >
                  <span className="text-white font-black text-lg">{b.week}</span>
                </div>
              </div>
              <p className={`text-xs font-bold ${b.is_on ? c.text : "text-zinc-600"}`}>{t(tierKey)}</p>
              <p className="text-zinc-500 text-[10px] mt-0.5">{b.sold}/{b.target} vape ({b.pct}%)</p>
            </div>
          );
        })}
      </div>

      {/* Credit Score Bar */}
      <div className="mb-2 flex items-center justify-between">
        <span className="text-zinc-400 text-xs uppercase tracking-wider">{t("credit_score")}</span>
        <span className={`font-black text-3xl ${sc.text}`} data-testid="credit-score-value">{data.credit_score}<span className="text-sm text-zinc-500 font-normal">/100</span></span>
      </div>
      <div className="h-4 bg-zinc-800 rounded-full overflow-hidden relative" data-testid="credit-score-bar">
        <div
          className="h-full transition-all rounded-full"
          style={{
            width: `${data.credit_score}%`,
            background: `linear-gradient(90deg, ${sc.bg} 0%, ${sc.bg} 100%)`,
            boxShadow: `0 0 12px ${sc.glow}`,
          }}
        />
        <div className="absolute inset-0 flex items-center justify-between px-0.5 pointer-events-none">
          <div className="w-px h-full bg-zinc-700 ml-[15%]" title="15%" />
          <div className="w-px h-full bg-zinc-700" title="50%" style={{marginLeft: "35%"}} />
          <div className="w-px h-full bg-zinc-700" title="70%" style={{marginLeft: "20%"}} />
          <div className="w-px h-full bg-zinc-700" title="90%" style={{marginLeft: "20%"}} />
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
        <span>0</span><span className="text-red-400">15 ⚠</span><span>50</span><span>70</span><span>90</span><span>100</span>
      </div>

      {/* Risk banner - only show if backend risk_message present, kept as backend text (server-side i18n TBD) */}
      {data.risk_message && (
        <div className={`mt-3 rounded-lg p-3 border-2 ${data.losing_contract_alert ? "bg-red-950/50 border-red-500 animate-pulse" : "bg-orange-950/40 border-orange-500/50"}`} data-testid="risk-banner">
          <p className={`text-sm font-bold flex items-center gap-2 ${data.losing_contract_alert ? "text-red-300" : "text-orange-300"}`}>
            <AlertTriangle className="w-4 h-4" /> {data.risk_message}
          </p>
        </div>
      )}

      {/* Explanation with detailed reasons */}
      <div className="mt-3 bg-zinc-900/40 rounded-lg p-3 text-xs">
        <p className="text-zinc-400 font-bold uppercase text-[10px] mb-2">📋 {t("why_this_score")}</p>
        {(data.reasons || []).length === 0 ? (
          <p className="text-emerald-400">✅ {t("no_penalty_msg")}</p>
        ) : (
          <div className="space-y-1">
            {data.reasons.map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-zinc-950/50 p-1.5 rounded">
                <span className="text-zinc-300">{parseReasonLabel(r.label, t)}</span>
                <span className="text-red-400 font-bold">{r.penalty}pt</span>
                <span className="text-zinc-500 text-[10px]">{parseReasonDetail(r.detail, t)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 grid grid-cols-2 gap-1 text-[10px] text-zinc-500">
          {data.contract_risk && <span className="col-span-2 text-red-400 font-bold">{t("contract_risk_msg")}</span>}
          {data.risk_account && <span className="col-span-2 text-red-400">{t("risk_account_msg")}</span>}
          {data.is_currently_late && data.days_overdue > 3 && <span className="col-span-2 text-orange-400">{t("late_days_msg_prefix")} ({data.days_overdue}j)</span>}
        </div>
      </div>
    </div>
  );
};

export default CreditScoreCard;
